import { useState, type FormEvent } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { useUpdateTariff, type TariffDefaults } from '@/data/fleets';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { TableScroll } from '@/components/ui/table-scroll';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/auth/api';
import { cn } from '@/lib/cn';

// ============================================================
// Rate card editor (U9): the numbers behind the portal's
// /estimate and /tariff. Whole card saves in one PUT.
// Extracted from settings.tsx for the signup wizard (setup-wizard
// unit) — Settings and the wizard's "Confirm your rates" step
// render the SAME component; behaviour is unchanged.
// ============================================================

const VEHICLE_LABEL: Partial<Record<string, string>> = {
  cycle: 'Cycle',
  cargo_bike: 'Cargo bike',
  motorcycle: 'Motorcycle',
  car: 'Car',
  small_van: 'Small van',
  mwb: 'MWB',
  lwb: 'LWB',
  luton: 'Luton',
  xlwb: 'XLWB',
  jumbo_luton: 'Jumbo Luton',
};

interface TariffFormBand {
  label: string;
  vehicleTypes: TariffDefaults['bands'][number]['vehicleTypes'];
  /** Pence per mile, as typed (integer pence). */
  rate: string;
  /** Minimum charge in pounds, as typed. */
  minimum: string;
  pricedOnConfirmation: boolean;
}

export interface TariffForm {
  vatRatePercent: string;
  oohPercent: string;
  oohStartHour: string;
  oohEndHour: string;
  oohWeekends: boolean;
  oohBankHolidays: boolean;
  roadFactor: string;
  /** Pounds, as typed. */
  extraStop: string;
  waitingRate: string;
  waitingFreeMinutes: string;
  surcharges: { label: string; amount: string }[];
  bands: TariffFormBand[];
}

const pounds = (pence: number | null): string =>
  pence === null ? '' : (pence / 100).toFixed(2);
const parsePounds = (s: string): number => Math.round(Number(s) * 100);

export function seedTariffForm(card: TariffDefaults): TariffForm {
  return {
    vatRatePercent: String(card.vatRatePercent),
    oohPercent: String(card.oohPercent),
    oohStartHour: String(card.oohStartHour),
    oohEndHour: String(card.oohEndHour),
    oohWeekends: card.oohWeekends,
    oohBankHolidays: card.oohBankHolidays,
    roadFactor: String(card.roadFactor),
    extraStop: pounds(card.extraStopFeePence),
    waitingRate: pounds(card.waitingRatePence),
    waitingFreeMinutes: String(card.waitingFreeMinutes),
    surcharges: card.surcharges.map((s) => ({
      label: s.label,
      amount: pounds(s.amountPence),
    })),
    bands: card.bands.map((b) => ({
      label: b.label,
      vehicleTypes: b.vehicleTypes,
      rate: b.ratePerMilePence === null ? '' : String(b.ratePerMilePence),
      minimum: pounds(b.minimumPence),
      pricedOnConfirmation: b.pricedOnConfirmation,
    })),
  };
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-2 py-2 text-2xs font-medium uppercase tracking-wider text-breaker-ink-2 first:pl-0 last:pr-0',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={cn('px-2 py-3 align-middle first:pl-0 last:pr-0', className)}>
      {children}
    </td>
  );
}

export function RateCardEditor({
  fleetId,
  canEdit,
  stored,
  initial,
  submitLabel = 'Save rate card',
  onSaved,
}: {
  fleetId: string;
  canEdit: boolean;
  stored: boolean;
  initial: TariffForm;
  /** Wizard override — Settings keeps the default. */
  submitLabel?: string;
  /** Wizard hook: advance the step after a successful save. */
  onSaved?: () => void;
}) {
  const toast = useToast();
  const save = useUpdateTariff(fleetId);
  // Unit D: Enter-key re-submits are dropped while one is in flight.
  const submitLock = useActionLock();
  const [form, setForm] = useState(initial);
  const disabled = !canEdit;

  const setField = (k: keyof TariffForm) => (v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setBand = (i: number, patch: Partial<TariffFormBand>) =>
    setForm((f) => ({
      ...f,
      bands: f.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    }));

  function submit(e: FormEvent) {
    e.preventDefault();
    const bad = (v: number) => Number.isNaN(v) || v < 0;
    const body = {
      vatRatePercent: Number(form.vatRatePercent),
      oohPercent: Number(form.oohPercent),
      oohStartHour: Number(form.oohStartHour),
      oohEndHour: Number(form.oohEndHour),
      oohWeekends: form.oohWeekends,
      oohBankHolidays: form.oohBankHolidays,
      roadFactor: Number(form.roadFactor),
      extraStopFeePence: parsePounds(form.extraStop || '0'),
      waitingRatePence: parsePounds(form.waitingRate || '0'),
      waitingFreeMinutes: Number(form.waitingFreeMinutes || '0'),
      surcharges: form.surcharges
        .filter((s) => s.label.trim() !== '')
        .map((s) => ({ label: s.label.trim(), amountPence: parsePounds(s.amount || '0') })),
      bands: form.bands.map((b) => ({
        label: b.label.trim(),
        vehicleTypes: b.vehicleTypes,
        ratePerMilePence: b.pricedOnConfirmation ? null : Number(b.rate),
        minimumPence: b.pricedOnConfirmation ? null : parsePounds(b.minimum || '0'),
        pricedOnConfirmation: b.pricedOnConfirmation,
      })),
    };
    if (
      bad(body.vatRatePercent) ||
      bad(body.oohPercent) ||
      bad(body.roadFactor) ||
      body.bands.some(
        (b) =>
          !b.pricedOnConfirmation &&
          (bad(b.ratePerMilePence ?? NaN) || bad(b.minimumPence ?? NaN)),
      )
    ) {
      toast.error('Check the numbers — something is missing or negative.');
      return;
    }
    submitLock.run(() =>
      save
        .mutateAsync(body)
        .then(() => {
          toast.success('Rate card saved — the portal now prices off it.');
          onSaved?.();
        })
        .catch((err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not save the rate card.'),
        ),
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {!stored ? (
        <p className="text-xs text-breaker-mute rounded-md bg-breaker-ground-2 border border-breaker-line px-3 py-2">
          Using platform defaults — saving stores your own card and the portal
          prices on it from then on.
        </p>
      ) : null}

      {/* Band ladder */}
      <TableScroll>
      {/* min-w so the ladder scrolls inside TableScroll at 390px instead of
          crushing the Band and Minimum inputs. */}
      <table className="w-full min-w-[620px] text-sm">
        <thead>
          <tr className="text-left">
            <Th className="w-[130px]">Band</Th>
            <Th className="w-[180px]">Vehicles</Th>
            <Th className="w-[110px]">Rate (p/mile)</Th>
            <Th className="w-[110px]">Minimum (£)</Th>
            <Th className="w-[90px]">POC</Th>
          </tr>
        </thead>
        <tbody>
          {form.bands.map((b, i) => (
            <tr key={i} className="border-t border-breaker-line">
              <Td>
                <Input
                  value={b.label}
                  disabled={disabled}
                  onChange={(e) => setBand(i, { label: e.target.value })}
                />
              </Td>
              <Td>
                <span className="text-xs text-breaker-mute">
                  {b.vehicleTypes.map((v) => VEHICLE_LABEL[v] ?? v).join(', ')}
                </span>
              </Td>
              <Td>
                <Input
                  inputMode="numeric"
                  value={b.rate}
                  placeholder={b.pricedOnConfirmation ? '—' : undefined}
                  disabled={disabled || b.pricedOnConfirmation}
                  onChange={(e) => setBand(i, { rate: e.target.value })}
                  className="tabular-nums"
                />
              </Td>
              <Td>
                <Input
                  inputMode="decimal"
                  value={b.minimum}
                  placeholder={b.pricedOnConfirmation ? '—' : undefined}
                  disabled={disabled || b.pricedOnConfirmation}
                  onChange={(e) => setBand(i, { minimum: e.target.value })}
                  className="tabular-nums"
                />
              </Td>
              <Td>
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={b.pricedOnConfirmation}
                  disabled={disabled}
                  onChange={(e) => setBand(i, { pricedOnConfirmation: e.target.checked })}
                  title="Priced on confirmation — no estimate shown"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>

      {/* Scalars */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-breaker-line pt-4">
        <Field label="VAT %" htmlFor="tfVat">
          <Input id="tfVat" inputMode="decimal" value={form.vatRatePercent} disabled={disabled} onChange={(e) => setField('vatRatePercent')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="Out-of-hours %" htmlFor="tfOoh">
          <Input id="tfOoh" inputMode="decimal" value={form.oohPercent} disabled={disabled} onChange={(e) => setField('oohPercent')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="OOH from (hour)" htmlFor="tfOohStart">
          <Input id="tfOohStart" inputMode="numeric" value={form.oohStartHour} disabled={disabled} onChange={(e) => setField('oohStartHour')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="OOH until (hour)" htmlFor="tfOohEnd">
          <Input id="tfOohEnd" inputMode="numeric" value={form.oohEndHour} disabled={disabled} onChange={(e) => setField('oohEndHour')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="Road factor" htmlFor="tfRoad" hint="Straight-line miles × this">
          <Input id="tfRoad" inputMode="decimal" value={form.roadFactor} disabled={disabled} onChange={(e) => setField('roadFactor')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="Extra stop (£)" htmlFor="tfStop">
          <Input id="tfStop" inputMode="decimal" value={form.extraStop} disabled={disabled} onChange={(e) => setField('extraStop')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="Waiting (£/hour)" htmlFor="tfWait">
          <Input id="tfWait" inputMode="decimal" value={form.waitingRate} disabled={disabled} onChange={(e) => setField('waitingRate')(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="Free waiting (min)" htmlFor="tfWaitFree">
          <Input id="tfWaitFree" inputMode="numeric" value={form.waitingFreeMinutes} disabled={disabled} onChange={(e) => setField('waitingFreeMinutes')(e.target.value)} className="tabular-nums" />
        </Field>
      </div>

      <div className="flex items-center gap-6">
        <label className="inline-flex items-center gap-2 text-sm text-breaker-ink">
          <input type="checkbox" className="h-4 w-4" checked={form.oohWeekends} disabled={disabled} onChange={(e) => setField('oohWeekends')(e.target.checked)} />
          Weekends out of hours
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-breaker-ink">
          <input type="checkbox" className="h-4 w-4" checked={form.oohBankHolidays} disabled={disabled} onChange={(e) => setField('oohBankHolidays')(e.target.checked)} />
          Bank holidays out of hours
        </label>
      </div>

      {/* Surcharges */}
      <div className="border-t border-breaker-line pt-4 space-y-2">
        <p className="text-sm font-medium text-breaker-ink">Surcharges</p>
        <p className="text-xs text-breaker-mute">
          Billed after the job alongside waiting time — never part of the
          customer&rsquo;s estimate.
        </p>
        {form.surcharges.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={s.label}
              placeholder="Congestion charge"
              disabled={disabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  surcharges: f.surcharges.map((x, j) =>
                    j === i ? { ...x, label: e.target.value } : x,
                  ),
                }))
              }
            />
            <Input
              inputMode="decimal"
              value={s.amount}
              placeholder="15.00"
              className="max-w-[120px] tabular-nums"
              disabled={disabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  surcharges: f.surcharges.map((x, j) =>
                    j === i ? { ...x, amount: e.target.value } : x,
                  ),
                }))
              }
            />
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    surcharges: f.surcharges.filter((_, j) => j !== i),
                  }))
                }
              >
                Remove
              </Button>
            ) : null}
          </div>
        ))}
        {canEdit ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setForm((f) => ({
                ...f,
                surcharges: [...f.surcharges, { label: '', amount: '' }],
              }))
            }
          >
            Add surcharge
          </Button>
        ) : null}
      </div>

      {canEdit ? (
        <div className="flex justify-end border-t border-breaker-line pt-4">
          <Button type="submit" size="sm" loading={save.isPending || submitLock.locked}>
            {submitLabel}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
